// ./src/components/features/authentication/AuthLayout/AuthLayout.tsx
import React, { ReactNode } from "react";
import {
  Box,
  Stack,
  Title,
  Text,
  Group,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconRocket,
  IconCloud,
  IconLock,
} from "@tabler/icons-react";
import { Logo } from "../../generic/Logo";

export interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  variant?: "signin" | "signup" | "forgot-password";
}

// Floating geometric shapes for visual interest
const FloatingShapes = () => {
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <Box
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "40%",
          height: "40%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${theme.colors.accent[4]}30 0%, transparent 70%)`,
        }}
      />
      <Box
        style={{
          position: "absolute",
          bottom: "10%",
          left: "-10%",
          width: "35%",
          height: "35%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${theme.colors.brand[4]}25 0%, transparent 70%)`,
        }}
      />
      <Box
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(${theme.colors.brand[3]}08 1px, transparent 1px),
            linear-gradient(90deg, ${theme.colors.brand[3]}08 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
        }}
      />
    </Box>
  );
};

const features = [
  { icon: IconShieldCheck, text: "27 field types with server-side validation" },
  { icon: IconRocket, text: "Point any HTML form and go live in minutes" },
  { icon: IconCloud, text: "Bring your own email and storage services" },
  { icon: IconLock, text: "reCAPTCHA and honeypot bot protection" },
];

// Brand panel component (left side on desktop)
const BrandPanel = () => {
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        position: "relative",
        background: `linear-gradient(165deg, ${theme.colors.brand[9]} 0%, ${theme.colors.brand[7]} 40%, ${theme.colors.brand[5]} 100%)`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "2rem",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <FloatingShapes />

      <Stack
        gap="lg"
        style={{ position: "relative", zIndex: 1, maxWidth: 380 }}
      >
        {/* Logo with filter to make it white on dark background */}
        <Box style={{ filter: "brightness(0) invert(1)" }}>
          <Logo size="medium" />
        </Box>

        <Box>
          <Text
            size="lg"
            fw={700}
            style={{
              color: "#ffffff",
              lineHeight: 1.2,
              fontSize: "1.5rem",
            }}
          >
            The Backend for{" "}
            <Text
              component="span"
              inherit
              style={{
                background: `linear-gradient(90deg, ${theme.colors.accent[3]} 0%, ${theme.colors.accent[1]} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Your Forms
            </Text>
          </Text>
          <Text size="sm" c={theme.colors.brand[2]} mt="xs">
            Validation, security, and integrations — all handled.
          </Text>
        </Box>

        <Stack gap="xs">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Group key={index} gap="sm" wrap="nowrap">
                <ThemeIcon
                  size={28}
                  radius="sm"
                  variant="filled"
                  style={{
                    backgroundColor: `${theme.colors.accent[4]}20`,
                    border: `1px solid ${theme.colors.accent[4]}30`,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={14} color={theme.colors.accent[2]} />
                </ThemeIcon>
                <Text size="xs" c={theme.colors.brand[1]}>
                  {feature.text}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

// Mobile brand header
const MobileBrandHeader = () => {
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        background: `linear-gradient(135deg, ${theme.colors.brand[9]} 0%, ${theme.colors.brand[6]} 100%)`,
        padding: "0.75rem 1.5rem 0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box style={{ filter: "brightness(0) invert(1)" }}>
        <Logo size="small" />
      </Box>
    </Box>
  );
};

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  subtitle,
}) => {
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.colors.neutral[0],
      }}
    >
      {/* Mobile header */}
      <Box hiddenFrom="md">
        <MobileBrandHeader />
      </Box>

      <Box style={{ flex: 1, display: "flex", flexDirection: "row" }}>
        {/* Brand panel - desktop only */}
        <Box
          visibleFrom="md"
          style={{
            flex: "0 0 42%",
            maxWidth: "480px",
            minHeight: "100vh",
          }}
        >
          <BrandPanel />
        </Box>

        {/* Form panel */}
        <Box
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem",
          }}
        >
          <Box style={{ width: "100%", maxWidth: 360 }}>
            {title && (
              <Box mb="sm">
                <Title
                  order={1}
                  size="h3"
                  fw={700}
                  c={theme.colors.neutral[8]}
                  style={{
                    fontFamily: theme.headings.fontFamily,
                    lineHeight: 1.2,
                  }}
                >
                  {title}
                </Title>
                {subtitle && (
                  <Text size="sm" c={theme.colors.neutral[5]} mt={4}>
                    {subtitle}
                  </Text>
                )}
              </Box>
            )}

            {children}

            <Text size="xs" c={theme.colors.neutral[4]} ta="center" mt="md">
              By continuing, you agree to our{" "}
              <Text
                component="a"
                href="https://formsubmits.com/terms"
                size="xs"
                c={theme.colors.brand[5]}
                td="none"
              >
                Terms
              </Text>{" "}
              and{" "}
              <Text
                component="a"
                href="https://formsubmits.com/privacy"
                size="xs"
                c={theme.colors.brand[5]}
                td="none"
              >
                Privacy Policy
              </Text>
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
